import { Link } from "react-router-dom";
import { motion } from "framer-motion";

// Redesigned per design.md (14islands-derived monochrome editorial
// system), then adjusted after direct feedback that a pure-typography
// Hero read as too austere. Removed entirely: colored ambient blob
// decorations, gradient backgrounds, the original SVG illustration,
// filled pill buttons, and glow/blur effects - all explicitly against
// spec ("Do not introduce any color", "Do not use shadows, glows", "No
// illustrations, no abstract graphics"). A real photograph replaced the
// illustration (photography, not illustration, is explicitly the
// system's sanctioned visual medium) - the headline no longer bleeds
// full-width edge-to-edge as the spec's literal pattern describes, since
// it now shares the row with that image in a two-column layout.
//
// The LCP fix from the earlier performance session is preserved
// deliberately: the H1 and the intro paragraph below it stay plain,
// unanimated elements - do not wrap either in a fade-in. Both were
// candidates for the Lighthouse-measured LCP element given their size;
// the paragraph was the one actually confirmed by the audit at the time.
// That measurement predates the hero image being added back in - the
// image itself is now a plausible new LCP candidate at this size and is
// marked eager/high-priority accordingly, but this has NOT been
// re-verified with an actual Lighthouse run since. Everything else in
// the Hero (eyebrow tag, CTA links) is far smaller and was never a
// plausible LCP candidate, so those get their entrance motion back -
// stripping animation from the whole section rather than just the
// measured element was an overcorrection from that earlier fix.

export const HeroSection = () => {
  const scrollToServices = () => {
    // Routed through Lenis, same fix as PortfolioScroll's jump-to-item
    // dots and Footer's "Back to top" - native scrollIntoView desyncs
    // from Lenis's internal scroll position, which was the cause of the
    // random-jump bug reported earlier in this session.
    const el = document.getElementById("services");
    if (!el) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(el, { immediate: false });
    } else {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      className="relative bg-paper pt-40 md:pt-[108px] pb-20 md:pb-[160px]"
      data-testid="hero-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-[1.3fr_1fr] gap-12 lg:gap-16 items-center">
        <div>
        {/* Uppercase Eyebrow Tag - small text, negligible LCP risk */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-bentonsans text-caption uppercase tracking-wide text-stone mb-5"
        >
          Custom Software Development
          <br />
          London, United Kingdom
        </motion.p>

        {/* Display Headline Block - "That" rendered in Stone as the
            spec's single sanctioned typographic contrast device (used
            for ampersands/connecting words). Deliberately unanimated -
            see note above. Sized down slightly from the full 180px given
            it now shares the row with a real photo rather than bleeding
            edge-to-edge alone. */}
        <h1 className="font-aftenscreen font-normal text-ink text-[52px] sm:text-[76px] lg:text-[92px] leading-[0.9] tracking-[-0.03em] mb-10">
          Systems
          <br />
          <span className="text-stone">That</span> Scale.
        </h1>

        {/* LCP element - deliberately unanimated, see note above. */}
        <p className="font-bentonsans text-body leading-[1.4] text-graphite max-w-lg mb-10">
          LOLCorp is a UK-based custom software development company. We
          design and build secure, scalable systems for growing
          businesses. 4-6 week projects starting at £10,000 GBP. <br /><br />We work with
          growing businesses that have outgrown Excel, email, and
          disconnected tools. We design and build custom internal systems
          that replace spreadsheets with clarity, control, and scalability —
          without forcing your operations into off-the-shelf software
        </p>

        {/* Text-only ghost links - no filled button anywhere per spec
            ("the link IS the button"). This is a real, deliberate trade
            against a more conversion-optimised filled CTA - flagged
            plainly rather than silently absorbed. Small text, negligible
            LCP risk, so entrance motion restored here too. */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="flex flex-col sm:flex-row gap-6 sm:gap-10"
        >
          <Link
            to="/consultation"
            className="font-bentonsans text-body text-ink hover:text-stone transition-colors duration-300"
            data-testid="hero-cta-consultation"
          >
            Book Consultation →
          </Link>
          <button
            onClick={scrollToServices}
            className="font-bentonsans text-body text-ink hover:text-stone transition-colors duration-300 text-left"
            data-testid="hero-cta-services"
          >
            Explore Services →
          </button>
        </motion.div>
        </div>

        {/* Real LOLCorp brand photography, per direct request - replaces
            the Unsplash stock photo used previously. Marked high-
            priority/eager since it's above the fold and, at this size,
            is a plausible LCP candidate. This hasn't been re-verified
            with an actual Lighthouse run since the photo swap; worth
            doing before assuming the earlier LCP fix still fully holds. */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="hidden lg:block"
        >
          <img
            src="https://assets.lolcdn.xyz/lolcorp-hero-main.png"
            alt="LOLCorp - custom software development"
            className="w-full aspect-[4/5] object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </motion.div>
      </div>
    </section>
  );
};
